import { Color } from 'pixi.js';
import { buildChildrenForSceneUsingData } from '../libs/buildChildrenForScene';
import {
    getStringMetadata,
    getAnimatableAttributes,
    templateAlphaByIndex,
    templateColorByIndex,
    templateFontsByIndex,
    templateWatermarkSourcesByIndex,
    getTemplateIndexFromAssetProperty,
    getTemplateIndex,
    getTemplateProperty,
    setTemplateProperty,
    isTemplateReference,
    getNumberMetadata,
} from '../core/attributes';
import { getGlobalFontManager } from '../core/FontManager';
import { Template } from '../core/template/Template';
import { getResolution } from '../libs/videoData';
import { Scene } from '../core/scene/Scene';
import { getAspectRatioDisplayName, getAspectRatioDisplayRatio } from '../aspectRatioConfig';

import { PlaybackRegions } from '../core/PlaybackRegions';
import { MediaGeometry } from '../core/asset/media/MediaGeometry';
import { Config } from './config';

import { getPartialSceneDesignConfig } from './designs/scenes';
import { sliceVideoData } from './sliceVideoData';
import duplicateAssetData from './duplicateAssetData';

/**
 * Video Data Class
 *
 * IMPORTANT
 * All methods & variables here are exposed externally (via the "API") so any changes require ensuring
 * that any code using VideoData (in Luminary and outside) have been updated!
 */
export class VideoData extends Config {
    // scene designs
    static getPartialSceneDesignConfig = getPartialSceneDesignConfig;

    static getGlobalFontManager = getGlobalFontManager;
    static getStringMetadata = getStringMetadata;
    static getNumberMetadata = getNumberMetadata;
    static getAnimatableAttributes = getAnimatableAttributes;

    static rgb2hex = (rgb: number[] | Float32Array) => new Color(rgb).toNumber();
    static hex2rgb = (hex: number, out?: number[]) => {
        const c = new Color(hex).toRgbArray();
        if (out) {
            out[0] = c[0];
            out[1] = c[1];
            out[2] = c[2];
            return out;
        }
        return Array.from(c);
    };
    static hex2string = (hex: number) => new Color(hex).toHex();
    static string2hex = (str: string) => new Color(str).toNumber();

    static templateAlphaByIndex = templateAlphaByIndex;
    static templateColorByIndex = templateColorByIndex;
    static templateFontsByIndex = templateFontsByIndex;
    static templateWatermarkSourcesByIndex = templateWatermarkSourcesByIndex;

    static getTemplateIndexFromAssetProperty = getTemplateIndexFromAssetProperty;
    static getTemplateProperty = getTemplateProperty;
    static setTemplateProperty = setTemplateProperty;
    static isTemplateReference = isTemplateReference;
    static getTemplateIndex = getTemplateIndex;

    // template data helpers
    static duplicateTemplateData = Template.duplicateTemplateData;
    static getColorsFromTemplateData = Template.getColorsFromTemplateData;
    static getFontsFromTemplateData = Template.getFontsFromTemplateData;
    static getWatermarkSourcesFromTemplateData = Template.getWatermarkSourcesFromTemplateData;

    // scene data helpers (lazy to avoid circular dependency TDZ)
    static duplicateSceneData(...args: Parameters<typeof Scene.duplicateSceneData>) {
        return Scene.duplicateSceneData(...args);
    }
    static getSceneCategoryFromSceneData(
        ...args: Parameters<typeof Scene.getSceneCategoryFromSceneData>
    ) {
        return Scene.getSceneCategoryFromSceneData(...args);
    }

    static sliceVideoData = sliceVideoData;
    static getResolution = getResolution;
    static buildChildrenForSceneUsingData = buildChildrenForSceneUsingData;
    static duplicateAssetData = duplicateAssetData;

    // playback regions utilities
    static getCutRegions = PlaybackRegions.getCutRegions;
    static assetTimeToPlaybackTime = PlaybackRegions.assetTimeToPlaybackTime;
    static playbackTimeToAssetTime = PlaybackRegions.playbackTimeToAssetTime;

    // aspect ratio utilities
    static getAspectRatioDisplayName = getAspectRatioDisplayName;
    static getAspectRatioDisplayRatio = getAspectRatioDisplayRatio;

    // media geometry utilities
    static getMediaTranslationLimit = MediaGeometry.translationLimitFrom;
}
